import http from './http';

// Task 16 (Plataforma): consumo do CRUD de tenants + super-admins da API.
// Todas as rotas abaixo ficam atrás do middleware `super.admin` no backend
// (ver alodesk-chat-api routes/api.php) -- o front NUNCA deve tratar o
// gating de UI como a barreira de segurança real, só como conveniência.

export async function listTenants() {
  const { data } = await http.get('/tenants');
  return data?.tenants || [];
}

// payload: { name, slug, admin_name, admin_email, admin_password? }
// Retorno inclui `generated_password` em texto puro SÓ quando admin_password
// foi omitido -- ver CreateTenantDialog para o tratamento (exibido uma
// única vez, nunca persistido).
export async function createTenant(payload) {
  const { data } = await http.post('/tenants', payload);
  return data;
}

export async function updateTenant(id, payload) {
  const { data } = await http.put(`/tenants/${id}`, payload);
  return data;
}

export async function updateTenantStatus(id, status) {
  const { data } = await http.put(`/tenants/${id}/status`, { status });
  return data;
}

export async function listSuperAdmins() {
  const { data } = await http.get('/super-admins');
  return data?.users || [];
}

// enabled precisa ser um boolean de verdade (a API é estrita quanto a isso
// -- ver docblock de SuperAdminUserController::toggle no backend).
export async function toggleSuperAdmin(id, enabled) {
  const { data } = await http.put(`/users/${id}/super-admin`, { enabled: !!enabled });
  return data;
}

// Busca um usuário por e-mail (match exato), CROSS-TENANT, para conceder
// super-admin -- GET /users/lookup no backend, atrás do middleware
// `super.admin`, roda dentro de `runWithoutScope()` (ver docblock de
// SuperAdminUserController::lookup). Substitui o antigo
// `findUserByEmailInOwnTenant` (que usava `/users/list`, escopado ao
// tenant do usuário logado -- por isso só achava usuários do MESMO tenant
// de quem pesquisava).
//
// 404 é um resultado VÁLIDO (usuário não encontrado com esse e-mail), não
// um erro -- devolve `null` nesse caso. Qualquer outro status (403 sem
// permissão, 422 e-mail inválido, 500) sobe para o chamador tratar.
export async function lookupUserByEmail(email) {
  try {
    const { data } = await http.get('/users/lookup', { params: { email } });
    return data?.user || null;
  } catch (error) {
    if (error?.response?.status === 404) {
      return null;
    }
    throw error;
  }
}

// ---------------------------------------------------------------------------
// Caixa de e-mail da empresa (Task: e-mail por tenant).
//
// A caixa era UMA, global, no `.env` da API, e todo chamado por e-mail nascia
// na SysFar. Agora cada empresa tem a sua. Estas rotas também ficam atrás de
// `super.admin`: são credenciais de servidor de e-mail.
//
// A senha NUNCA volta da API -- o `show` devolve `has_password`, não a senha.
// Por isso o `update` aceita senha vazia: significa "mantém a que está".

export async function getTenantMailSettings(tenantId) {
  const { data } = await http.get(`/tenants/${tenantId}/mail-settings`);
  return data?.data || null;
}

export async function saveTenantMailSettings(tenantId, payload) {
  const { data } = await http.put(`/tenants/${tenantId}/mail-settings`, payload);
  return data?.data || null;
}

export async function deleteTenantMailSettings(tenantId) {
  const { data } = await http.delete(`/tenants/${tenantId}/mail-settings`);
  return data;
}

// Testa a conexão IMAP sem ler nem marcar e-mail nenhum.
//
// Existe porque o modo de falha aqui é mudo: uma senha errada não quebra nada
// visível -- o canal só para de trazer chamado, e se descobre quando o cliente
// reclama que escreveu e ninguém respondeu.
//
// 422 é um resultado VÁLIDO (falhou a conexão, com o motivo), não um erro de
// programa: devolve `{ ok: false, error }` em vez de estourar.
export async function testTenantMailSettings(tenantId) {
  try {
    const { data } = await http.post(`/tenants/${tenantId}/mail-settings/test`);
    return { ok: true, msg: data?.msg };
  } catch (error) {
    if (error?.response?.status === 422) {
      return { ok: false, error: error.response.data?.error || 'Não foi possível conectar.' };
    }
    throw error;
  }
}
